/**
 * Product Metaobject Selector Component
 * Handles dropdown functionality for metaobject selection
 */
class ProductMetaobjectSelector extends HTMLElement {
  constructor() {
    super();
    this.dropdowns = this.querySelectorAll('.metaobject-dropdown');
    this.selectedValues = new Map();
  }

  connectedCallback() {
    this.initializeDropdowns();
    this.bindEvents();
  }

  initializeDropdowns() {
    this.dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.metaobject-dropdown__trigger');
      const list = dropdown.querySelector('.metaobject-dropdown__list');
      const options = dropdown.querySelectorAll('.metaobject-dropdown__option');

      trigger.setAttribute('aria-controls', this.generateId());
      list.setAttribute('id', trigger.getAttribute('aria-controls'));

    });
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const trigger = e.target.closest('.metaobject-dropdown__trigger');
      if (trigger) {
        e.preventDefault();
        this.toggleDropdown(trigger.parentElement);
      }
    });

    this.addEventListener('keydown', (e) => {
      this.handleKeyNavigation(e);
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeAllDropdowns();
      }
    });
  }

  /**
   * @param {HTMLElement} dropdown
   */
  toggleDropdown(dropdown) {
    const trigger = dropdown.querySelector('.metaobject-dropdown__trigger');
    const list = dropdown.querySelector('.metaobject-dropdown__list');
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    this.closeAllDropdowns();

    if (!isOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      list.hidden = false;
      
      const firstOption = list.querySelector('.metaobject-dropdown__option');
      if (firstOption) {
        firstOption.focus();
      }

      this.dispatchEvent(new CustomEvent('dropdown:open', {
        detail: { 
          category: trigger.dataset.category,
          dropdown 
        }
      }));
    }
  }

  closeAllDropdowns() {
    this.dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.metaobject-dropdown__trigger');
      const list = dropdown.querySelector('.metaobject-dropdown__list');
      
      trigger.setAttribute('aria-expanded', 'false');
      list.hidden = true;
    });
  }

  /**
   * @param {HTMLElement} dropdown
   * @param {HTMLElement} option
   * @param {Event} event
   */
  selectOption(dropdown, option, event) {
    event.preventDefault();
    event.stopPropagation();

    const trigger = dropdown.querySelector('.metaobject-dropdown__trigger');
    const label = trigger.querySelector('.metaobject-dropdown__label');
    const category = trigger.dataset.category;
    
    const value = option.dataset.value;
    const title = option.dataset.title;

    label.textContent = title;

    this.selectedValues.set(category, {
      value,
      title,
      category
    });

    this.closeAllDropdowns();

    this.dispatchEvent(new CustomEvent('metaobject:select', {
      detail: {
        category,
        value,
        title,
        allSelections: Array.from(this.selectedValues.values())
      }
    }));

  }

  /**
   * @param {KeyboardEvent} e
   */
  handleKeyNavigation(e) {
    const { key } = e;
    const activeElement = document.activeElement;
    
    switch (key) {
      case 'Escape':
        this.closeAllDropdowns();
        break;
        
      case 'ArrowDown':
      case 'ArrowUp':
        if (activeElement.classList.contains('metaobject-dropdown__option')) {
          e.preventDefault();
          this.navigateOptions(activeElement, key === 'ArrowDown' ? 1 : -1);
        }
        break;
        
      case 'Enter':
      case ' ':
        if (activeElement.classList.contains('metaobject-dropdown__option')) {
          e.preventDefault();
          activeElement.click();
        } else if (activeElement.classList.contains('metaobject-dropdown__trigger')) {
          e.preventDefault();
          this.toggleDropdown(activeElement.parentElement);
        }
        break;
    }
  }

  /**
   * @param {HTMLElement} currentOption
   * @param {number} direction - 1 for down, -1 for up
   */
  navigateOptions(currentOption, direction) {
    const dropdown = currentOption.closest('.metaobject-dropdown');
    const options = Array.from(dropdown.querySelectorAll('.metaobject-dropdown__option'));
    const currentIndex = options.indexOf(currentOption);
    
    let nextIndex = currentIndex + direction;
    
    if (nextIndex < 0) {
      nextIndex = options.length - 1;
    } else if (nextIndex >= options.length) {
      nextIndex = 0;
    }
    
    const nextOption = options[nextIndex];
    if (nextOption) {
      nextOption.focus();
    }
  }

  generateId() {
    return `metaobject-dropdown-${Math.random().toString(36).substr(2, 9)}`;
  }

  getSelectedValues() {
    return Array.from(this.selectedValues.values());
  }

  reset() {
    this.selectedValues.clear();
    this.dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('.metaobject-dropdown__trigger');
      const label = trigger.querySelector('.metaobject-dropdown__label');
      label.textContent = trigger.dataset.category;
    });
    this.closeAllDropdowns();
  }
}

if (!customElements.get('product-metaobject-selector')) {
  customElements.define('product-metaobject-selector', ProductMetaobjectSelector);
}